import { api } from '../api/client.js';
import { getVisitorId, newSessionId } from './visitor.js';

interface QueuedEvent {
  type: string;
  linkId: string;
  proposalId?: string;
  sessionId: string;
  visitorId: string;
  occurredAt: string;
  pageUrl: string;
  viewport: { width: number; height: number };
  [extra: string]: unknown;
}

export class RoomTracker {
  private readonly queue: QueuedEvent[] = [];
  // DeckLens 주입 스크립트에 동일 신원을 넘기기 위해 공개 (읽기 전용)
  readonly sessionId: string;
  readonly visitorId: string;
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly linkId: string) {
    this.sessionId = newSessionId();
    this.visitorId = getVisitorId();
  }

  start(): void {
    this.flushTimer = setInterval(() => void this.flush(), 5000);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('beforeunload', this.onUnload);
  }

  stop(): void {
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.flushTimer = null;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('beforeunload', this.onUnload);
    void this.flush(true);
  }

  enqueue(type: string, data: Record<string, unknown> = {}): void {
    this.queue.push({
      type,
      linkId: this.linkId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      occurredAt: new Date().toISOString(),
      pageUrl: location.href,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      ...data,
    });
  }

  async flush(useBeacon = false): Promise<void> {
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    const payload = JSON.stringify({ events });
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon('/api/events', new Blob([payload], { type: 'application/json' }));
      return;
    }
    try {
      await api.postEvents({ events: events as unknown as { type: string; sessionId: string }[] });
    } catch {
      // best-effort
    }
  }

  private onVisibility = (): void => {
    if (document.visibilityState === 'hidden') void this.flush(true);
  };

  private onUnload = (): void => {
    this.enqueue('session_end', { target: 'room' });
    void this.flush(true);
  };
}
